import {
  addMyEmail,
  addMyPhone,
  type EmailVerificationFlow,
  type PhoneVerificationFlow,
  resendMyEmailCode,
  resendMyPhoneCode,
  setPrimaryEmail,
  setPrimaryPhone,
  updateMyUserProfile,
  verifyMyEmail,
  verifyMyPhone,
} from '@echo/api-client/onearly';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { IdentifierChannel } from '../auth/session';
import { startIdentifierVerification } from '../domain/profile';
import { queryKeys } from '../query/keys';
import { useAnalytics } from './provider';

/** Core's fetch client returns `{ status, data }` for every documented status; only 200 carries the payload. */
function ok<T extends { status: number; data: unknown }>(
  response: T,
): Extract<T, { status: 200 }>['data'] {
  if (response.status !== 200) throw new Error(`OnEarly: unexpected status ${response.status}`);
  return response.data as Extract<T, { status: 200 }>['data'];
}

export type IdentifierVerification =
  | { channel: 'phone'; identifier: string; flow: PhoneVerificationFlow }
  | { channel: 'email'; identifier: string; flow: EmailVerificationFlow };

/**
 * "Complete your profile" (docs/mvp/02-ONBOARDING.md): the names go straight to the profile;
 * the optional second identifier goes through Core's verification flow like in OnEarly `app`
 * (add → code → verify). `me` is refetched only by `finish` / `verify`, so the session gate
 * lets the person through once the screen is really done — with names alone the profile is
 * already complete (PRD-1958), and refetching after the names would end the screen before a
 * pending code could be entered.
 */
export function useCompleteProfile() {
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const refreshMe = () => queryClient.invalidateQueries({ queryKey: queryKeys.me() });
  const completed = async () => {
    await refreshMe();
    analytics.track({ name: 'profile_completed' });
  };

  const saveNames = useMutation({
    mutationFn: async (input: { firstName: string; lastName: string }) => {
      return ok(
        await updateMyUserProfile({
          firstName: input.firstName.trim(),
          lastName: input.lastName.trim(),
        }),
      );
    },
  });

  /** Done without a second identifier (or skipping its code): refetch `me` so the gate moves on. */
  const finish = useMutation({ mutationFn: completed });

  const startVerification = useMutation({
    mutationFn: async (input: {
      channel: IdentifierChannel;
      identifier: string;
    }): Promise<IdentifierVerification> => {
      if (input.channel === 'phone') {
        const phoneNumber = input.identifier;
        const flow = await startIdentifierVerification(
          {
            add: async () => ok(await addMyPhone({ phoneNumber })).verification,
            resend: async () => ok(await resendMyPhoneCode({ phoneNumber })).verification,
          },
          'phone',
        );
        return { channel: 'phone', identifier: phoneNumber, flow };
      }
      const email = input.identifier;
      const flow = await startIdentifierVerification(
        {
          add: async () => ok(await addMyEmail({ email })).verification,
          resend: async () => ok(await resendMyEmailCode({ email })).verification,
        },
        'email',
      );
      return { channel: 'email', identifier: email, flow };
    },
  });

  const resend = useMutation({
    mutationFn: async (current: IdentifierVerification): Promise<IdentifierVerification> => {
      if (current.channel === 'phone') {
        const { verification } = ok(await resendMyPhoneCode({ phoneNumber: current.identifier }));
        if (!verification) throw new Error('OnEarly: no verification flow was returned');
        return { ...current, flow: verification };
      }
      const { verification } = ok(await resendMyEmailCode({ email: current.identifier }));
      if (!verification) throw new Error('OnEarly: no verification flow was returned');
      return { ...current, flow: verification };
    },
  });

  const verify = useMutation({
    mutationFn: async (input: { verification: IdentifierVerification; code: string }) => {
      const { verification, code } = input;
      // A verified secondary becomes the profile identifier only once it is primary; a
      // person completing their profile has no other one, so promote it right away.
      if (verification.channel === 'phone') {
        const { phones } = ok(await verifyMyPhone({ ...verification.flow, code }));
        if (!phones.some((entry) => entry.primary && entry.verified)) {
          await setPrimaryPhone({ phoneNumber: verification.identifier });
        }
      } else {
        const { emails } = ok(await verifyMyEmail({ ...verification.flow, code }));
        if (!emails.some((entry) => entry.primary && entry.verified)) {
          await setPrimaryEmail({ email: verification.identifier });
        }
      }
    },
    onSuccess: completed,
  });

  return { saveNames, startVerification, resend, verify, finish };
}
